import type { MappedMessage, MessageThread } from './message-mapping';

/**
 * Which thread to open for a citation.
 *
 * A citation of one message is shown inside its thread. The thread id from
 * hydrate (parent chat / mobileThreadId) wins when it matches a listed thread;
 * otherwise the current selection (or the first thread) is used so browse
 * still lands on ``threads[0]``.
 */
export function selectFocusedThread(args: {
  threads: MessageThread[];
  threadId: string | number | null;
  currentKey: string | null;
}): string | null {
  const { threads, threadId, currentKey } = args;
  if (threads.length === 0) return currentKey;

  if (threadId != null && String(threadId) !== '') {
    const key = String(threadId);
    if (threads.some((thread) => thread.key === key)) return key;
  }

  return currentKey ?? threads[0]?.key ?? null;
}

export function isFocusedMessage(
  message: MappedMessage,
  focusIds: Array<string | number>,
): boolean {
  if (focusIds.length === 0) return false;
  return focusIds.map(String).includes(message.id);
}

/** ``3 of 47 messages in this thread`` — omitted when nothing was cited. */
export function citationWindowLabel(citedCount: number, total: number): string | null {
  if (citedCount <= 0 || total <= 0) return null;
  const cited = Math.min(citedCount, total);
  const noun = total === 1 ? 'message' : 'messages';
  return `${cited} of ${total} ${noun} in this thread`;
}

/** ``3 of 47 points in this time range`` — omitted when nothing was cited. */
export function citationMapWindowLabel(citedCount: number, total: number): string | null {
  if (citedCount <= 0 || total <= 0) return null;
  const cited = Math.min(citedCount, total);
  const noun = total === 1 ? 'point' : 'points';
  return `${cited} of ${total} ${noun} in this time range`;
}
